import QRCode from "react-qr-code"
import { useEffect, useState } from "react"
import { format } from "date-fns"

import { USER_ACCOUNT_LOADING_STATUS } from "../../../ClientAuthorization/Default/common/constant"

import { Box, Img, Text, TextWithIcon, Button } from "ui-kit/components-branded/index"
import { useClientContext } from "modules/ClientAuthorization/common/ClientContext"
import { useGetTelegramStartUrl, useScrollByUrlHash } from "modules/ClientAccount/hooks"
import style from "modules/ClientAccount/components/TelegramAccountCard/style.module.scss"
import { TelegramBlock } from "components"
import { TelegramAccount } from "modules/ClientAuthorization/common/interfaces"
import { isVerified } from "modules/ClientAuthorization/common/utils"

const formatCounter = (num: number | undefined) => {
    if (num === undefined) return "00:00"

    return format(num * 1000, "mm:ss")
}
function AttachTelegramAccountCard() {
    useScrollByUrlHash()
    const { data, refetch, isFetching } = useGetTelegramStartUrl()
    const [url, setUrl] = useState<string>()
    const [counter, setCounter] = useState<number>()

    useEffect(() => {
        if (isFetching || data === undefined) return
        if (url !== undefined && counter !== undefined) return

        setUrl(data.url)
        setCounter(300)
    }, [data, isFetching])

    useEffect(() => {
        if (counter === undefined) return

        if (counter === 0) {
            setUrl(undefined)
            setCounter(undefined)
            return
        }

        const timer =
            counter >= 0
                ? setInterval(() => {
                      setCounter(counter - 1)
                  }, 1000)
                : undefined
        return () => clearInterval(timer)
    }, [counter])

    const onClick = () => {
        void refetch()
    }

    if (url === undefined) {
        return (
            <TelegramBlock
                button={
                    <Button
                        color="primary"
                        className={`${style.button} w-100`}
                        onClick={onClick}
                        disabled={isFetching}
                    >
                        Подключить
                    </Button>
                }
            />
        )
    }

    return (
        <Box className={style.container} id="attach-tg">
            <div className={`${style.content} ${style.contentColumn}`}>
                <div className={style.description}>
                    <Text className={style.title}>Подключите уведомления в&nbsp;Telegram</Text>
                    <Text className={style.subtitle}>
                        Отсканируйте QR-код или перейдите{" "}
                        <a href={url} className="is-underlined">
                            по&nbsp;ссылке
                        </a>
                        .
                    </Text>
                </div>
                <QRCode
                    size={256}
                    style={{ height: "auto", maxWidth: "294px", width: "294px" }}
                    value={url}
                    viewBox={`0 0 256 256`}
                    className={style.qrCode}
                />
                <Text className={style.alert}>
                    <TextWithIcon
                        icon={<Img className={style.alertEmoji} src="attention.png" />}
                        position="left"
                    >
                        Не&nbsp;передавайте ссылку и&nbsp;QR-код третьим лицам.
                    </TextWithIcon>
                </Text>
                <hr className={style.hr} />
                <Text className={style.timer}>
                    Деактивируем ссылку и QR-код через {formatCounter(counter)}
                </Text>
            </div>
        </Box>
    )
}

function TelegramAccountAttachedCard({ username }: TelegramAccount) {
    return (
        <Box className={style.container}>
            <div className={style.content}>
                <div className={style.description}>
                    <div>
                        <TextWithIcon
                            icon={<Img className={style.titleEmoji} src="white_check_mark.png" />}
                            position="left"
                            isAccent
                            size="h3"
                            className={style.title}
                        >
                            Уведомления в&nbsp;Telegram подключены для @{username}
                        </TextWithIcon>
                    </div>
                    <Text className={style.subtitle}>
                        Мы&nbsp;продолжим отправлять все уведомления по&nbsp;почте,
                        но&nbsp;не&nbsp;будем присылать СМС. Если хотите снова получать СМС,
                        заблокируйте бота.
                    </Text>
                </div>
                <Img className={style.image} src="client-account-telegram-account.svg" />
            </div>
        </Box>
    )
}

type Loading = {
    kind: "LOADING"
    telegramAccount: undefined
}
type AttachTelegramAccount = {
    kind: "ATTACH_TELEGRAM_ACCOUNT"
    telegramAccount: undefined
}
type TelegramAccountAttached = {
    kind: "TELEGRAM_ACCOUNT_ATTACHED"
    telegramAccount: TelegramAccount
}
type TelegramAccountState = Loading | AttachTelegramAccount | TelegramAccountAttached

export function TelegramAccountCard() {
    const [{ userAccount, loading }] = useClientContext()
    const [state, setState] = useState<TelegramAccountState>({
        kind: "LOADING",
        telegramAccount: undefined,
    })

    useEffect(() => {
        if (loading !== USER_ACCOUNT_LOADING_STATUS.READY) {
            setState({ kind: "LOADING", telegramAccount: undefined })
            return
        }

        if (!isVerified(userAccount)) {
            setState({ kind: "LOADING", telegramAccount: undefined })
            return
        }

        if (!userAccount.telegram_account) {
            setState({ kind: "ATTACH_TELEGRAM_ACCOUNT", telegramAccount: undefined })
            return
        }

        setState({
            kind: "TELEGRAM_ACCOUNT_ATTACHED",
            telegramAccount: userAccount.telegram_account,
        })
    }, [loading, userAccount])

    switch (state.kind) {
        case "LOADING":
            return null
        case "ATTACH_TELEGRAM_ACCOUNT":
            return <AttachTelegramAccountCard />
        case "TELEGRAM_ACCOUNT_ATTACHED":
            return <TelegramAccountAttachedCard username={state.telegramAccount.username} />
    }
}
